import { BaseObj } from '@/objects/BaseObj';
import { useBaseObjEntityStateHandler } from '@/objects/entitystatehandler/entitystatehandler';
import { isRef, ref, unref } from 'vue';
import type { CommunicationType } from './../enums/CommunicationType';
import type { EntityState } from './../enums/EntityState';
import { formatDateIso } from '@/utilities/dateutils';

export class CommunicationToApprove extends BaseObj {
    public ID: number;
    public __id: number;

    public Approved: boolean;
    public __approved: boolean;

    public Changed: Date;
    public __changed: Date;

    public CommunicationType: CommunicationType;
    public __communicationtype: CommunicationType;

    public Content: string;
    public __content: string;

    public Created: Date;
    public __created: Date;

    public EventName: string;
    public __eventname: string;

    public IsNewsletter: boolean;
    public __isnewsletter: boolean;

    public Name: string;
    public __name: string;

    public Sender: string;
    public __sender: string;

    public __entityStateHandler;
    public __selfEntityState: EntityState;
    public __entityState: any;
    public get EntityState(): EntityState {
        return unref(this.__entityState);
    }
    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        ID?: number;
        Approved?: boolean;
        Changed?: Date;
        CommunicationType?: CommunicationType;
        Content?: string | null;
        Created?: Date;
        EntityState?: EntityState;
        EventName?: string | null;
        IsNewsletter?: boolean;
        Name?: string | null;
        Sender?: string | null;
    }) {
        super();
        this.__entityState = ref(values.EntityState ?? 'Unchanged');
        this.__selfEntityState = values.EntityState ?? 'Unchanged';
        this.__entityStateHandler = useBaseObjEntityStateHandler<CommunicationToApprove>(unref(this.__entityState), (states) => {
            if (isRef(this.__entityState)) {
                this.__entityState.value = states.EntityStateWithChildrenConsidered;
            } else {
                console.warn('CommunicationToApprove tried to set entitystate, but entitystate is not a ref! This should never happen');
            }
            this.__selfEntityState = states.SelfEntityState;
        });
        this.ID = values.ID ?? 0;
        this.__id = values.ID ?? 0;
        Object.defineProperty(this, 'ID', {
            get(): number {
                return this.__id;
            },
            enumerable: true,
            configurable: true,
        });

        this.Approved = values.Approved ?? false;
        this.__approved = values.Approved ?? false;
        Object.defineProperty(this, 'Approved', {
            get(): boolean {
                return this.__approved;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('Approved', this.__approved, value);
                this.__approved = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Changed = values.Changed ?? new Date('1970-01-01T00:00:00.000');
        this.__changed = values.Changed ?? new Date('1970-01-01T00:00:00.000');
        Object.defineProperty(this, 'Changed', {
            get(): Date {
                return this.__changed;
            },
            set(value: Date) {
                this.__entityStateHandler.propertyChanged('Changed', this.__changed, value);
                this.__changed = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.CommunicationType = values.CommunicationType ?? 'SMS';
        this.__communicationtype = values.CommunicationType ?? 'SMS';
        Object.defineProperty(this, 'CommunicationType', {
            get(): CommunicationType {
                return this.__communicationtype;
            },
            set(value: CommunicationType) {
                this.__entityStateHandler.propertyChanged('CommunicationType', this.__communicationtype, value);
                this.__communicationtype = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Content = values.Content ?? '';
        this.__content = values.Content ?? '';
        Object.defineProperty(this, 'Content', {
            get(): string {
                return this.__content;
            },
            set(value: string) {
                this.__entityStateHandler.propertyChanged('Content', this.__content, value);
                this.__content = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Created = values.Created ?? new Date('1970-01-01T00:00:00.000');
        this.__created = values.Created ?? new Date('1970-01-01T00:00:00.000');
        Object.defineProperty(this, 'Created', {
            get(): Date {
                return this.__created;
            },
            set(value: Date) {
                this.__entityStateHandler.propertyChanged('Created', this.__created, value);
                this.__created = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.EventName = values.EventName ?? '';
        this.__eventname = values.EventName ?? '';
        Object.defineProperty(this, 'EventName', {
            get(): string {
                return this.__eventname;
            },
            set(value: string) {
                this.__entityStateHandler.propertyChanged('EventName', this.__eventname, value);
                this.__eventname = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.IsNewsletter = values.IsNewsletter ?? false;
        this.__isnewsletter = values.IsNewsletter ?? false;
        Object.defineProperty(this, 'IsNewsletter', {
            get(): boolean {
                return this.__isnewsletter;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('IsNewsletter', this.__isnewsletter, value);
                this.__isnewsletter = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Name = values.Name ?? '';
        this.__name = values.Name ?? '';
        Object.defineProperty(this, 'Name', {
            get(): string {
                return this.__name;
            },
            set(value: string) {
                this.__entityStateHandler.propertyChanged('Name', this.__name, value);
                this.__name = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Sender = values.Sender ?? '';
        this.__sender = values.Sender ?? '';
        Object.defineProperty(this, 'Sender', {
            get(): string {
                return this.__sender;
            },
            set(value: string) {
                this.__entityStateHandler.propertyChanged('Sender', this.__sender, value);
                this.__sender = value;
            },
            enumerable: true,
            configurable: true,
        });
    }

    public _setNotifyParentOfModificationCallback(callback: () => void) {
        this.__entityStateHandler.setNotifyParentOfModificationCallback(callback);
    }

    public serialize(): string {
        return (
            '{' +
            `"ID": ${JSON.stringify(this.ID)}` +
            ',' +
            `"Approved": ${JSON.stringify(this.Approved)}` +
            ',' +
            `"Changed": ${'"' + formatDateIso(this.Changed) + '"'}` +
            ',' +
            `"CommunicationType": ${this.CommunicationType == 'SMS' ? 1 : this.CommunicationType == 'Email' ? 2 : this.CommunicationType == 'SignupConfirmation' ? 3 : this.CommunicationType == 'Invoice' ? 4 : this.CommunicationType == 'StartCardEmail' ? 5 : this.CommunicationType == 'StartCardSMS' ? 6 : this.CommunicationType == 'Giftcard' ? 7 : this.CommunicationType == 'CancelRefund' ? 8 : this.CommunicationType == 'Transfer' ? 9 : this.CommunicationType == 'Transfer2' ? 10 : this.CommunicationType == 'RelayContestantEdit' ? 11 : 1}` +
            ',' +
            `"Content": ${JSON.stringify(this.Content)}` +
            ',' +
            `"Created": ${'"' + formatDateIso(this.Created) + '"'}` +
            ',' +
            `"EntityState": ${unref(this.__selfEntityState) == 'Unchanged' ? 0 : unref(this.__selfEntityState) == 'Deleted' ? 1 : unref(this.__selfEntityState) == 'Modified' ? 2 : unref(this.__selfEntityState) == 'Added' ? 3 : 0}` +
            ',' +
            `"EventName": ${JSON.stringify(this.EventName)}` +
            ',' +
            `"IsNewsletter": ${JSON.stringify(this.IsNewsletter)}` +
            ',' +
            `"Name": ${JSON.stringify(this.Name)}` +
            ',' +
            `"Sender": ${JSON.stringify(this.Sender)}` +
            ',' +
            `"ChangedColumns": ${unref(this.__selfEntityState) !== 'Modified' ? 'null' : JSON.stringify(this.__entityStateHandler.getChangedPropertiesArray())}` +
            '}'
        );
    }

    /**
     * Sets the entity state to Deleted
     */
    public Delete() {
        if (this.__selfEntityState === 'Added') {
            throw new Error('Cannot delete an object that is in Added state. It has not been saved yet.');
        }
        this.__selfEntityState = 'Deleted';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Undeletes the object by setting resetting the EntityState
     *
     * If the object has modifications, the new state will be 'Modified', otherwise 'Unchanged'
     */
    public DeleteUndo() {
        this.__selfEntityState = 'Unchanged';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Sets the entity state to Added
     *
     * Also sets the ID to the supplied ID. If no id parameter supplied, the previous id is kept
     *
     * Note: This does not do anything with the children, so use with caution
     *
     * The use case of this is to make copies of existing objects as new objects without having to fill every field.
     */
    public SetEntityStateAsAddedAndResetId(id?: number) {
        if (this.EntityState !== 'Unchanged') {
            throw new Error('Changing a modified object to Added is not supported.');
        }
        this.__selfEntityState = 'Added';
        if (id !== undefined) {
            this.__id = id;
        }
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Creates an instance of CommunicationToApprove from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        ID?: number;
        Approved?: boolean;
        Changed?: Date;
        CommunicationType?: CommunicationType;
        Content?: string | null;
        Created?: Date;
        EventName?: string | null;
        IsNewsletter?: boolean;
        Name?: string | null;
        Sender?: string | null;
        EntityState?: 'Added' | 'Unchanged';
    }): CommunicationToApprove {
        return new CommunicationToApprove(values);
    }

    public static _FromServerJson(json: any): CommunicationToApprove {
        return new CommunicationToApprove({
            ID: json.ID,
            Approved: json.Approved,
            Changed: json.Changed instanceof Date ? json.Changed : new Date(json.Changed),
            CommunicationType:
                json.CommunicationType == 1
                    ? 'SMS'
                    : json.CommunicationType == 2
                      ? 'Email'
                      : json.CommunicationType == 3
                        ? 'SignupConfirmation'
                        : json.CommunicationType == 4
                          ? 'Invoice'
                          : json.CommunicationType == 5
                            ? 'StartCardEmail'
                            : json.CommunicationType == 6
                              ? 'StartCardSMS'
                              : json.CommunicationType == 7
                                ? 'Giftcard'
                                : json.CommunicationType == 8
                                  ? 'CancelRefund'
                                  : json.CommunicationType == 9
                                    ? 'Transfer'
                                    : json.CommunicationType == 10
                                      ? 'Transfer2'
                                      : json.CommunicationType == 11
                                        ? 'RelayContestantEdit'
                                        : 'SMS',
            Content: json.Content ?? '',
            Created: json.Created instanceof Date ? json.Created : new Date(json.Created),
            EventName: json.EventName ?? '',
            IsNewsletter: json.IsNewsletter,
            Name: json.Name ?? '',
            Sender: json.Sender ?? '',
            EntityState: 'Unchanged',
        });
    }

    /**
     * Creates an instance of CommunicationToApprove from another instance of CommunicationToApprove.
     * @param other The CommunicationToApprove you want to clone
     * @returns A cloned CommunicationToApprove
     */
    public static FromOther(other: CommunicationToApprove): CommunicationToApprove {
        if (other.EntityState !== 'Unchanged') {
            throw new Error('Cloning an object with EntityState other than Unchanged is not supported');
        }
        return CommunicationToApprove._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return CommunicationToApprove.FromOther(this);
    }

    /**
     * IsPropertyChanged.
     */
    public IsPropertyChanged(
        ...props: (
            | 'ID'
            | 'Approved'
            | 'Changed'
            | 'CommunicationType'
            | 'Content'
            | 'Created'
            | 'EventName'
            | 'IsNewsletter'
            | 'Name'
            | 'Sender'
        )[]
    ) {
        return this.__entityStateHandler.isPropertyChanged(...props);
    }
    static NAME_OF_CLASS = 'CommunicationToApprove';
}
